import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { backendService } from "../../../../services/backend.service";
import type { DepartmentJobPpdOverride } from "../../../../utils/models";

/**
 * Pending PPD-inclusion edits, flushed on Save like every other control on this page. `true`/`false`
 * set an override to that value; `null` clears it so the job falls back to its catalog default. A job
 * absent from the map is untouched — and the writers below keep it that way, so a non-empty map always
 * means there is genuinely something to send.
 */
export type PendingJobPpdOverrides = Record<string, boolean | null>;

export const jobPpdOverridesQueryKey = (departmentId: string) => ["deptJobPpdOverrides", departmentId] as const;

/** The minimum set of writes that turns `persisted` into what `pending` asks for. */
export type JobPpdOverrideFlush = {
	upserts: { jobId: string; excludeFromPpd: boolean }[];
	deletes: string[];
};

/**
 * A job's effective PPD inclusion: a pending edit wins, then the department override, then the job's
 * own catalog default. `isOverridden` (the reset-to-default affordance) follows the same resolution,
 * so the icon can never claim an override the switch is no longer showing.
 */
export function resolveJobPpd(
	pending: PendingJobPpdOverrides,
	jobId: string,
	persistedExcluded: boolean | undefined,
	defaultExcluded: boolean,
): { excluded: boolean; isOverridden: boolean } {
	if (jobId in pending) {
		const value = pending[jobId];
		if (value === null) return { excluded: defaultExcluded, isOverridden: false };
		return { excluded: value, isOverridden: true };
	}
	if (persistedExcluded === undefined) return { excluded: defaultExcluded, isOverridden: false };
	return { excluded: persistedExcluded, isOverridden: true };
}

/**
 * Records one row's new PPD inclusion. Two normalizations, both load-bearing: a value equal to the
 * job's catalog default is stored as `null` (a DELETE, which is what the immediate-save version did),
 * and an edit that lands back on what the server already holds DROPS the entry entirely — so toggling
 * a switch twice leaves nothing to flush rather than a no-op request.
 */
export function setPendingJobPpd(
	pending: PendingJobPpdOverrides,
	jobId: string,
	nextExcluded: boolean,
	defaultExcluded: boolean,
	persistedExcluded: boolean | undefined,
): PendingJobPpdOverrides {
	const desired = nextExcluded === defaultExcluded ? null : nextExcluded;
	const persisted = persistedExcluded === undefined ? null : persistedExcluded;
	if (desired === persisted) return withoutPendingJobPpd(pending, jobId);
	return { ...pending, [jobId]: desired };
}

export function withoutPendingJobPpd(pending: PendingJobPpdOverrides, jobId: string): PendingJobPpdOverrides {
	if (!(jobId in pending)) return pending;
	const next = { ...pending };
	delete next[jobId];
	return next;
}

/**
 * Drops pending edits for jobs the department no longer enables, so the save cannot write an override
 * row for a job it is in the middle of removing. The route itself performs no membership check — it
 * would accept the write — which is exactly why the filter has to live here.
 */
export function prunePendingJobPpd(pending: PendingJobPpdOverrides, jobIds: string[]): PendingJobPpdOverrides {
	const enabled = new Set(jobIds);
	const next: PendingJobPpdOverrides = {};
	for (const [jobId, value] of Object.entries(pending)) if (enabled.has(jobId)) next[jobId] = value;
	return next;
}

/**
 * The pending entries a finished save did NOT spend. The flush reads the buffer once, when Save is
 * clicked; a toggle made while that save is in flight is not in it, and clearing the whole map on
 * success discarded that edit next to a success snackbar.
 */
export function pendingJobPpdAfterFlush(pending: PendingJobPpdOverrides, flushedJobIds: readonly string[]): PendingJobPpdOverrides {
	if (flushedJobIds.length === 0) return pending;
	const flushed = new Set(flushedJobIds);
	const next: PendingJobPpdOverrides = {};
	for (const [jobId, value] of Object.entries(pending)) if (!flushed.has(jobId)) next[jobId] = value;
	return next;
}

/**
 * A PPD write failed AFTER the config PUT already succeeded. Its own type so the save handler can
 * report `message` verbatim — that text is the only thing telling the operator the department DID
 * save — instead of preferring the wrapped axios `detail`, which says nothing about the config.
 */
export class JobPpdFlushError extends Error {
	/** Writes that DID land before the throw — a partial flush still changed PPD for those jobs. */
	readonly landed: number;

	constructor(message: string, landed: number, options?: { cause?: unknown }) {
		super(message, options);
		this.name = "JobPpdFlushError";
		this.landed = landed;
	}
}

export function diffPendingJobPpdOverrides(pending: PendingJobPpdOverrides, persisted: DepartmentJobPpdOverride[]): JobPpdOverrideFlush {
	const persistedById = new Map(persisted.map((override) => [override.jobId, override.excludeFromPpd]));
	const flush: JobPpdOverrideFlush = { upserts: [], deletes: [] };
	for (const [jobId, value] of Object.entries(pending)) {
		const current = persistedById.get(jobId);
		if (value === null) {
			// Unconditional: `persisted` comes from a cache entry that is evicted once the Shifts tab
			// unmounts (its query has no other observer), while the pending map lives on in page-level
			// form state and Save is reachable from almost every tab. Gating the DELETE on `current`
			// meant a clear made before that eviction flushed NOTHING and still reported success — the
			// row survived and the job stayed excluded. The route 404s on an absent row rather than
			// no-opping, so the flush treats that 404 as the desired state (see `deleteOverride`).
			flush.deletes.push(jobId);
		} else if (current !== value) {
			flush.upserts.push({ jobId, excludeFromPpd: value });
		}
	}
	return flush;
}

/**
 * Applies the pending map on Save, and reports whether anything actually went out (the caller uses
 * that to raise the wage-breakdown prompt ONCE per save rather than once per toggle).
 *
 * Throws if any write fails: the pending map then survives, so the next Save retries exactly the
 * calls that did not land. The retry is idempotent — an upsert that landed no longer differs from the
 * refreshed overrides and emits nothing, and a clear that landed re-sends a DELETE the route 404s,
 * which `deleteOverride` accepts as the state the clear asked for.
 */
export function useFlushJobPpdOverrides(departmentId: string) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();
	const { refreshNursesData } = useHospitalData();

	return useCallback(
		async (pending: PendingJobPpdOverrides): Promise<boolean> => {
			if (Object.keys(pending).length === 0) return false;
			// An absent cache entry reads as "no overrides". That is only a redundant-write optimisation
			// now — a clear flushes regardless (see `diffPendingJobPpdOverrides`), so an evicted or failed
			// query can cost an extra request but can no longer drop an edit.
			const persisted = queryClient.getQueryData<DepartmentJobPpdOverride[]>(jobPpdOverridesQueryKey(departmentId)) ?? [];
			const { upserts, deletes } = diffPendingJobPpdOverrides(pending, persisted);
			if (upserts.length === 0 && deletes.length === 0) return false;

			const failures: unknown[] = [];
			const attempt = async (write: () => Promise<unknown>, jobId: string) => {
				try {
					await write();
				} catch (error) {
					failures.push(error);
					trackError("dept_job_ppd_override_flush_error", {
						department_id: departmentId,
						job_id: jobId,
						error_message: error instanceof Error ? error.message : String(error),
					});
				}
			};
			const deleteOverride = async (jobId: string) => {
				try {
					await backendService.deleteDepartmentJobPpdOverride(departmentId, jobId);
				} catch (error) {
					// A clear can outlive its row: the config PUT's `sync_special_job_ppd_overrides` deletes
					// special-job rows behind this cache. The route's 404 is a real error for a direct API
					// caller, but here "already absent" IS the state the clear asked for — and without this
					// the retry documented above could only 404 again, forever.
					if (isAxiosError(error) && error.response?.status === 404) return;
					throw error;
				}
			};
			await Promise.all([
				...upserts.map((vars) => attempt(() => backendService.upsertDepartmentJobPpdOverride(departmentId, vars), vars.jobId)),
				...deletes.map((jobId) => attempt(() => deleteOverride(jobId), jobId)),
			]);

			await queryClient.invalidateQueries({ queryKey: jobPpdOverridesQueryKey(departmentId) });
			// `hospitalData` (Day Schedule) caches each shift's resolved `excludeFromPpd`, computed at query
			// time from the override + job default, so the stale snapshot has to be refetched here.
			void refreshNursesData(departmentId);

			const attempted = upserts.length + deletes.length;
			// One summary per save. The mutation collapses every per-job outcome into a single throw, so
			// without this a partial flush is indistinguishable from a total one, and the per-job error
			// events have no denominator.
			track("dept_job_ppd_override_flush", {
				department_id: departmentId,
				upserts: upserts.length,
				deletes: deletes.length,
				failures: failures.length,
			});

			if (failures.length > 0) {
				throw new JobPpdFlushError(t("department.config.jobs.ppd.flushFailed", { count: failures.length }), attempted - failures.length, {
					cause: failures[0],
				});
			}
			return true;
		},
		[queryClient, departmentId, refreshNursesData, t, track, trackError],
	);
}
